/* eslint-disable react/prop-types */
import cx from "classnames";
import { Component, useCallback } from "react";
import { jt, t } from "ttag";
import _ from "underscore";

import { ErrorMessage } from "metabase/common/components/ErrorMessage";
import ButtonsS from "metabase/css/components/buttons.module.css";
import CS from "metabase/css/core/index.css";
import { isEmbeddingSdk } from "metabase/embedding-sdk/config";
import { CreateOrEditQuestionAlertModalWithQuestion } from "metabase/notifications/modals/CreateOrEditQuestionAlertModal/CreateOrEditQuestionAlertModal";
import { ALERT_TYPE_ROWS, getAlertType } from "metabase/notifications/utils";
import { resetRowZoom, zoomInRow } from "metabase/query_builder/actions";
import {
  getIsShowingRawTable,
  getRowIndexToPKMap,
  getUiControls,
  getZoomedObjectId,
} from "metabase/query_builder/selectors";
import { useDispatch, useSelector } from "metabase/redux";
import Visualization from "metabase/visualizations/components/Visualization";
import * as Lib from "metabase-lib";
import { datasetContainsNoResults } from "metabase-lib/v1/queries/utils/dataset";

const ALLOWED_VISUALIZATION_PROPS = [
  // Table
  "isShowingDetailsOnlyColumns",
  // Table Interactive
  "hasMetadataPopovers",
  "tableHeaderHeight",
  "scrollToColumn",
  "renderTableHeader",
  "mode",
  "renderEmptyMessage",
  "zoomedRowIndex",
  // Legend
  "hideLegend",
];

export function VisualizationResult(props) {
  const dispatch = useDispatch();
  const isRawTable = useSelector(getIsShowingRawTable);
  const scrollToLastColumn = useSelector(
    (state) => getUiControls(state)?.scrollToLastColumn ?? false,
  );
  const rowIndexToPkMap = useSelector(getRowIndexToPKMap);
  const zoomedObjectId = useSelector(getZoomedObjectId);
  const onZoomRow = useCallback(
    (rowIndex) => {
      const objectId = rowIndexToPkMap?.[rowIndex] ?? rowIndex;
      if (objectId === zoomedObjectId) {
        dispatch(resetRowZoom());
      } else {
        dispatch(zoomInRow({ objectId }));
      }
    },
    [dispatch, rowIndexToPkMap, zoomedObjectId],
  );
  return (
    <VisualizationResultInner
      {...props}
      isRawTable={isRawTable}
      scrollToLastColumn={scrollToLastColumn}
      onZoomRow={onZoomRow}
    />
  );
}

class VisualizationResultInner extends Component {
  state = {
    showCreateAlertModal: false,
  };

  showCreateAlertModal = () => {
    this.setState({ showCreateAlertModal: true });
  };

  onCloseCreateAlertModal = () => {
    this.setState({ showCreateAlertModal: false });
  };

  render() {
    const {
      question,
      token,
      isDirty,
      queryBuilderMode,
      navigateToNewCardInsideQB,
      result,
      rawSeries,
      timelineEvents,
      selectedTimelineEventIds,
      onNavigateBack,
      className,
      isRunning,
      isShowingSummarySidebar,
      editSummary,
      renderEmptyMessage,
      isRawTable,
      scrollToLastColumn,
    } = this.props;
    const { showCreateAlertModal } = this.state;

    const noResults = datasetContainsNoResults(result.data);
    if (noResults && !isRunning && !renderEmptyMessage) {
      const supportsRowsPresentAlert =
        !isEmbeddingSdk() && getAlertType(question) === ALERT_TYPE_ROWS;

      const supportsBackToPreviousResult =
        !isEmbeddingSdk() || !!onNavigateBack;

      // successful query but there were 0 rows returned with the result
      return (
        <div className={cx(className, CS.flex)}>
          <ErrorMessage
            type="noRows"
            title={t`No results!`}
            message={t`This may be the answer you’re looking for. If not, try removing or changing your filters to make them less specific.`}
            action={
              <div>
                {supportsRowsPresentAlert && !isDirty && (
                  <p>
                    {jt`You can also ${(
                      <a
                        className={CS.link}
                        key="link"
                        onClick={this.showCreateAlertModal}
                      >
                        {t`get an alert`}
                      </a>
                    )} when there are some results.`}
                  </p>
                )}

                {supportsBackToPreviousResult && (
                  <button
                    className={ButtonsS.Button}
                    onClick={() =>
                      onNavigateBack ? onNavigateBack() : window.history.back()
                    }
                  >
                    {t`Back to previous results`}
                  </button>
                )}
              </div>
            }
          />
          {showCreateAlertModal && (
            <CreateOrEditQuestionAlertModalWithQuestion
              onClose={this.onCloseCreateAlertModal}
              onAlertCreated={this.onCloseCreateAlertModal}
            />
          )}
        </div>
      );
    } else {
      const vizSpecificProps = _.pick(
        this.props,
        ...ALLOWED_VISUALIZATION_PROPS,
      );
      const { isEditable } = Lib.queryDisplayInfo(question.query());
      const hasDrills = isEditable;
      return (
        <>
          <Visualization
            className={className}
            rawSeries={rawSeries}
            onChangeCardAndRun={
              hasDrills ? navigateToNewCardInsideQB : undefined
            }
            isEditing={true}
            isObjectDetail={false}
            isQueryBuilder={true}
            isRawTable={isRawTable}
            scrollToLastColumn={scrollToLastColumn}
            isShowingSummarySidebar={isShowingSummarySidebar}
            isRunning={isRunning}
            editSummary={editSummary}
            queryBuilderMode={queryBuilderMode}
            showTitle={false}
            canToggleSeriesVisibility
            metadata={question.metadata()}
            timelineEvents={timelineEvents}
            token={token}
            selectedTimelineEventIds={selectedTimelineEventIds}
            getExtraDataForClick={() => ({ zoomInRow })}
            onZoomRow={this.props.onZoomRow}
            handleVisualizationClick={this.props.handleVisualizationClick}
            onOpenTimelines={this.props.onOpenTimelines}
            onSelectTimelineEvents={this.props.selectTimelineEvents}
            onDeselectTimelineEvents={this.props.deselectTimelineEvents}
            onOpenChartSettings={this.props.onOpenChartSettings}
            onUpdateQuestion={this.props.onUpdateQuestion}
            onUpdateWarnings={this.props.onUpdateWarnings}
            onHeaderColumnReorder={this.props.onHeaderColumnReorder}
            onUpdateVisualizationSettings={
              this.props.onUpdateVisualizationSettings
            }
            onVisualizationRendered={this.props.onVisualizationRendered}
            {...vizSpecificProps}
          />
        </>
      );
    }
  }
}
